import PropTypes from "prop-types";
import { forwardRef } from "react";

// Ant Design
import { Card, Button, Divider } from "antd";
import { ArrowLeft } from '@/components/Icons';

// project import
import Highlighter from "./third-party/Highlighter";
import { useNavigate } from "react-router-dom";
import { useResponsive } from '@/hooks/useResponsive';
import { useTheme } from '@/App';

// ==============================|| CUSTOM - MAIN CARD ||============================== //

const MainCard = forwardRef(
  (
    {
      border = true,
      boxShadow,
      children,
      content = true,
      bodyStyle = {},
      darkTitle,
      elevation,
      secondary,
      shadow,
      footer,
      sx = {},
      title,
      codeHighlight,
      backbutton,
      customHeaderStyle,
      className = "",
      ...others
    },
    ref
  ) => {
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const { isDesktop } = useResponsive();

    // Build card className
    const cardClasses = [
      'm-0 lg:m-2.5',
      'rounded',
      border ? 'border' : 'border-none',
      border && (isDark ? 'border-gray-700' : 'border-gray-200'),
      boxShadow && 'shadow-md hover:shadow-lg',
      'transition-shadow duration-200',
      className
    ].filter(Boolean).join(' ');

    // Build header content
    let headerTitle = null;
    if (title) {
      // Use consistent font size for all pages - text-lg (18px)
      if (darkTitle) {
        headerTitle = <h3 className="text-lg font-bold m-0">{title}</h3>;
      } else {
        headerTitle = <span className="text-lg font-semibold">{title}</span>;
      }

      if (backbutton) {
        headerTitle = (
          <div className="flex justify-between items-center mx-0 lg:mx-2">
            {headerTitle}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              {isDesktop ? "Back" : ""}
            </Button>
          </div>
        );
      }
    } else if (backbutton) {
      headerTitle = (
        <div className="flex justify-end">
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              navigate(-1);
            }}
            icon={<ArrowLeft className="w-4 h-4" />}
          >
            {isDesktop ? "Back" : ""}
          </Button>
        </div>
      );
    }

    return (
      <Card
        ref={ref}
        className={cardClasses}
        title={headerTitle}
        extra={secondary}
        styles={{
          header: {
            padding: '7px',
            paddingBottom: 0,
            ...customHeaderStyle
          },
          body: content ? bodyStyle : { padding: 0 }
        }}
        {...others}
      >
        {/* Card content */}
        {content ? children : children}

        {/* Card footer */}
        {title && footer && (
          <div className="bg-gray-100 dark:bg-gray-800 pb-4 mt-4">
            {footer}
          </div>
        )}

        {/* Code highlighter */}
        {codeHighlight && (
          <>
            <Divider style={{ borderStyle: "dashed" }} />
            <Highlighter codeHighlight={codeHighlight} main>
              {children}
            </Highlighter>
          </>
        )}
      </Card>
    );
  }
);

MainCard.propTypes = {
  border: PropTypes.bool,
  boxShadow: PropTypes.bool,
  bodyStyle: PropTypes.object,
  darkTitle: PropTypes.bool,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  secondary: PropTypes.node,
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  codeHighlight: PropTypes.bool,
  content: PropTypes.bool,
  children: PropTypes.node,
  footer: PropTypes.node,
  backbutton: PropTypes.bool,
  customHeaderStyle: PropTypes.object,
  className: PropTypes.string,
};

export default MainCard;
